import React, { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { initializeSecurityRules } from './config/documentSecurity'
import { ToastProvider } from './components/common/ToastContainer'
import { OvacsNotificationProvider } from './contexts/OvacsNotificationContext'
import { PricingModeProvider } from './contexts/PricingModeContext'
import { BlockPolicyProvider } from './contexts/BlockPolicyContext'
import DevelopmentBanner from './components/common/DevelopmentBanner'
import AppUpdateWatcher from './components/common/AppUpdateWatcher'
import OnboardingWizard from './components/onboarding/OnboardingWizard'
import CheckoutSuccessPage from './pages/subscribe/CheckoutSuccessPage'
import GateModalHost from './components/gates/GateModalHost'
import RootErrorBoundary from './components/common/RootErrorBoundary'
import NotFoundPage from './pages/NotFoundPage'

// Layout Components
import PublicLayout from './components/layout/PublicLayout'
import PrivateLayout from './components/layout/PrivateLayout'
import SuperAdminLayout from './components/superadmin/SuperAdminLayout'
import SuperAdminRoute from './components/superadmin/SuperAdminRoute'

// Public Pages
import LandingPage from './pages/public/LandingPage'
import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage'
import TermsPage from './pages/public/TermsPage'

// Auth Pages
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import OTPVerificationPage from './pages/auth/OTPVerificationPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import OAuthCallbackPage from './pages/auth/OAuthCallbackPage'

// Dashboard Pages
import DashboardPage from './pages/dashboard/DashboardPage'

// Connection Pages
import ConnectionsPage from './pages/connections/ConnectionsPage'

// Notification Pages
import NotificationsPage from './pages/notifications/NotificationsPage'
import OvacsNotificationsPage from './pages/notifications/OvacsNotificationsPage'
import NotificationPreferencesPage from './pages/settings/NotificationPreferencesPage'

// Core Feature Pages
import CasesPage from './pages/cases/CasesPage'
import CaseDetailPage from './pages/cases/CaseDetailPage'
import CreateCasePage from './pages/cases/CreateCasePage'
import EditCasePage from './pages/cases/EditCasePage'
import ClientsPage from './pages/clients/ClientsPage'
import ClientDetailPage from './pages/clients/ClientDetailPage'
import CreateClientPage from './pages/clients/CreateClientPage'
import EditClientPage from './pages/clients/EditClientPage'
import DocumentsPage from './pages/documents/DocumentsPage'
import DocumentDetailPage from './pages/documents/DocumentDetailPage'
import DocumentGroupsPage from './pages/documents/DocumentGroupsPage'
import GroupDetailPage from './pages/documents/GroupDetailPage'
import SessionsPage from './pages/sessions/SessionsPage'
import SessionDetailPage from './pages/sessions/SessionDetailPage'
import CreateSessionPage from './pages/sessions/CreateSessionPage'
import EditSessionPage from './pages/sessions/EditSessionPage'
import MessagesPage from './pages/messages/MessagesPage'

// Profile Pages
import ProfileSettingsPage from './pages/profile/ProfileSettingsPage'

// Superadmin Pages
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard'
import SubscribersPage from './pages/superadmin/SubscribersPage'
import UserDetailPage from './pages/superadmin/UserDetailPage'
import CommunicationsPage from './pages/superadmin/CommunicationsPage'
import PermissionManagementPage from './pages/superadmin/PermissionManagementPage'
// Phase 3 — subscription management pages
import SuperadminSubscriptionsPage from './pages/superadmin/SubscriptionsPage'
import SuperadminSubscriptionDetailPage from './pages/superadmin/SubscriptionDetailPage'
import SuperadminSubscriptionSettingsPage from './pages/superadmin/SubscriptionSettingsPage'
import SuperadminStripeEventsPage from './pages/superadmin/StripeEventsPage'
import SuperadminMockPaymentsConsolePage from './pages/superadmin/MockPaymentsConsolePage'
// Landing CMS (June 2026)
import LandingCmsPage from './pages/superadmin/LandingCmsPage'
import AuthScreenCmsPage from './pages/superadmin/AuthScreenCmsPage'
import AppReleasesPage from './pages/superadmin/AppReleasesPage'
import MockPayPage from './pages/subscribe/MockPayPage'

// Public Pages
import ContactPage from './pages/public/ContactPage'

// Tutorial Pages
import TutorialsPage from './pages/TutorialsPage'

// Loading Component
import LoadingScreen from './components/ui/LoadingScreen'

// Debug Component (temporary) - DISABLED
// import AuthDebug from './components/debug/AuthDebug'

// Alert System
import { AlertProvider } from './contexts/AlertContext'
import AlertContainer from './components/ui/AlertContainer'

// Workspace Terminology
import { WorkspaceProvider } from './contexts/WorkspaceContext'

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth()

  // Fetch dynamic security rules from backend when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      initializeSecurityRules()
    }
  }, [isAuthenticated])

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <WorkspaceProvider>{children}</WorkspaceProvider>
}

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (isAuthenticated) {
    // Redirect superadmins to their dashboard
    if (user?.role === 'superadmin') {
      return <Navigate to="/superadmin" replace />
    }
    // Redirect assistants to communications page
    if (user?.role === 'assistant') {
      return <Navigate to="/superadmin/communications" replace />
    }
    return <Navigate to="/dashboard" replace />
  }

  return children
}

// Root Redirect Component (smart redirect based on auth and role)
const RootRedirect = () => {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (isAuthenticated) {
    // Redirect superadmins to their dashboard
    if (user?.role === 'superadmin') {
      return <Navigate to="/superadmin" replace />
    }
    // Redirect assistants to communications page
    if (user?.role === 'assistant') {
      return <Navigate to="/superadmin/communications" replace />
    }
    return <Navigate to="/dashboard" replace />
  }

  // Not authenticated - redirect to landing page
  return <Navigate to="/landing" replace />
}

function App() {
  return (
    <ToastProvider>
      <AlertProvider>
        <OvacsNotificationProvider>
          <PricingModeProvider>
          <BlockPolicyProvider>
          {/* Global Development Banner - Appears on all pages */}
          <DevelopmentBanner />
          {/* Loads a new deploy on the next page change, so nobody is stuck on an old build. */}
          <AppUpdateWatcher />
          {/* Drive-First post-signup onboarding wizard (Phase 3) */}
          <OnboardingWizard />
          {/* Global gate modal (Phase 3) — listens for 'ovacs:gate_blocked' events */}
          <GateModalHost />
        
        <div className="min-h-screen bg-gray-50" style={{ paddingTop: '30px' }}>
          {/* Temporary debug component - DISABLED */}
          {/* {import.meta.env.DEV && <AuthDebug />} */}
          
          {/* Global Alert Container */}
          <AlertContainer />

        <RootErrorBoundary>
        <Routes>
        {/* Public Routes */}
        <Route path="/" element={<RootRedirect />} />
        
        <Route
          path="/landing"
          element={<LandingPage />}
        />

        <Route
          path="/privacy"
          element={<PrivacyPolicyPage />}
        />

        <Route
          path="/terms"
          element={<TermsPage />}
        />

        {/* Stripe checkout success landing (Phase 3) */}
        <Route
          path="/subscribe/success"
          element={<CheckoutSuccessPage />}
        />

        {/* Mock-payment landing (Phase 3 — used when PAYMENT_GATEWAY='mock') */}
        <Route
          path="/subscribe/mock-pay"
          element={<MockPayPage />}
        />

        {/* Auth routes own their own chrome via <AuthShell> — no PublicLayout */}
        <Route
          path="/login"
          element={<PublicRoute><LoginPage /></PublicRoute>}
        />

        <Route
          path="/register"
          element={<PublicRoute><RegisterPage /></PublicRoute>}
        />

        <Route
          path="/verify-otp"
          element={<PublicRoute><OTPVerificationPage /></PublicRoute>}
        />

        <Route
          path="/forgot-password"
          element={<PublicRoute><ForgotPasswordPage /></PublicRoute>}
        />

        <Route
          path="/reset-password"
          element={<PublicRoute><ResetPasswordPage /></PublicRoute>}
        />

        <Route
          path="/contact"
          element={
            <ContactPage />
          }
        />

        <Route
          path="/tutorials"
          element={
            <TutorialsPage />
          }
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <DashboardPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile-settings"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <ProfileSettingsPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/connections"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <ConnectionsPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <OvacsNotificationsPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/notifications"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <NotificationPreferencesPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/cases"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <CasesPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/cases/create"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <CreateCasePage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/cases/:id"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <CaseDetailPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/cases/:id/edit"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <EditCasePage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/clients"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <ClientsPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/clients/create"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <CreateClientPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/clients/:id"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <ClientDetailPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/clients/:id/edit"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <EditClientPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        {/* Documents Routes */}
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <DocumentsPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents/:id"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <DocumentDetailPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/document-groups"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <DocumentGroupsPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/document-groups/:id"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <GroupDetailPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/sessions"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <SessionsPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/sessions/create"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <CreateSessionPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/sessions/:id"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <SessionDetailPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/sessions/:id/edit"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <EditSessionPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <DocumentsPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <MessagesPage />
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        {/* Superadmin Routes - Only accessible by role='superadmin' */}
        <Route
          path="/superadmin"
          element={
            <SuperAdminRoute>
              <SuperAdminLayout />
            </SuperAdminRoute>
          }
        >
          <Route index element={<SuperAdminDashboard />} />
          <Route path="subscribers" element={<SubscribersPage />} />
          <Route path="subscribers/:accountId" element={<UserDetailPage />} />
          <Route path="communications" element={<CommunicationsPage />} />
          <Route path="permissions" element={<PermissionManagementPage />} />
          {/* Phase 3 — Drive-First Pivot */}
          <Route path="subscriptions" element={<SuperadminSubscriptionsPage />} />
          <Route path="subscriptions/:subscriptionId" element={<SuperadminSubscriptionDetailPage />} />
          <Route path="subscription-settings" element={<SuperadminSubscriptionSettingsPage />} />
          <Route path="stripe-events" element={<SuperadminStripeEventsPage />} />
          <Route path="mock-payments" element={<SuperadminMockPaymentsConsolePage />} />
          {/* Landing CMS — June 2026 */}
          <Route path="landing-cms" element={<LandingCmsPage />} />
          {/* Auth screen brand panel CMS — June 2026 */}
          <Route path="auth-screen" element={<AuthScreenCmsPage />} />
          {/* Mobile app releases / force-update — July 2026 */}
          <Route path="app-releases" element={<AppReleasesPage />} />
        </Route>

        {/* OAuth callback (cloud storage integrations — Phase 1.5) */}
        <Route path="/oauth/google/callback" element={<OAuthCallbackPage />} />

        {/* Catch-all route — branded 404 page (NotFoundPage handles its own layout). */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </RootErrorBoundary>
        </div>
          </BlockPolicyProvider>
          </PricingModeProvider>
        </OvacsNotificationProvider>
      </AlertProvider>
    </ToastProvider>
  )
}

export default App
